import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
    main: {
        paddingBottom: theme.spacing(3),
        maxWidth: 600,
        margin: '0 auto',
    },
    section: {
        padding: theme.spacing(1.5),
    },
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minHeight: 44,
    },
    topBarBack: {
        width: 44,
        height: 44,
        marginLeft: -theme.spacing(1),
        padding: 0,
        borderRadius: '50%',
        color: theme.palette.text.primary,
        transition: theme.transitions.create('background-color', {
            duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
            backgroundColor: theme.palette.grey[100],
        },
    },
    topBarTitle: {
        flex: 1,
        textAlign: 'center',
        fontSize: '1.125rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    topBarSpacer: {
        width: 44,
        height: 44,
        flexShrink: 0,
    },
    card: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: 12,
        padding: theme.spacing(1.5),
        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
        border: `1px solid ${theme.palette.divider}`,
    },
    customerTitle: {
        fontSize: '1.125rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1.5),
    },
    actionRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
        minWidth: 0,
    },
    ctaButton: {
        flex: '1 1 0%',
        minWidth: 0,
        maxWidth: '100%',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        fontWeight: 600,
        padding: `${theme.spacing(1.5)}px ${theme.spacing(1)}px`,
        borderRadius: 12,
        minHeight: 44,
        textTransform: 'none',
        fontSize: '0.875rem',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    iconSquareButton: {
        flexShrink: 0,
        minWidth: 44,
        width: 44,
        height: 44,
        minHeight: 44,
        borderRadius: 12,
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.text.primary,
        padding: 0,
    },
    sectionTitle: {
        fontSize: '1rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1.5),
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: theme.spacing(2),
    },
    statCell: {
        textAlign: 'center',
    },
    statCellMiddle: {
        textAlign: 'center',
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
    },
    statValue: {
        fontSize: '1.125rem',
        fontWeight: 700,
        color: theme.palette.text.primary,
    },
    statLabel: {
        fontSize: '0.75rem',
        color: theme.palette.grey[600],
        marginTop: theme.spacing(0.5),
    },
    ordersScroll: {
        maxHeight: 384,
        overflowY: 'auto',
        marginRight: -4,
        paddingRight: 4,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
            display: 'none',
        },
    },
    orderBlock: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            borderBottom: 'none',
            marginBottom: 0,
            paddingBottom: 0,
        },
    },
    orderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    orderLink: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.text.primary,
        textDecoration: 'none',
    },
    orderAmount: {
        fontSize: '0.875rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    orderDate: {
        fontSize: '0.75rem',
        color: theme.palette.grey[600],
    },
    chip: {
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '4px 8px',
        borderRadius: 8,
    },
    /** Paid / delivered-style badge (e.g. Pagado) */
    chipSuccess: {
        backgroundColor: 'rgba(231, 249, 243, 1)',
        color: 'rgb(19, 195, 139)',
    },
    chipWarning: {
        backgroundColor: '#ffedd5',
        color: '#ea580c',
    },
    chipError: {
        backgroundColor: '#fee2e2',
        color: '#dc2626',
    },
    chipNeutral: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[700],
    },
    outlineButton: {
        width: '100%',
        marginTop: theme.spacing(0.5),
        border: `1px solid ${theme.palette.primary.main}`,
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        borderRadius: 12,
        minHeight: 44,
        padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    },
    loadMoreButton: {
        width: '100%',
        marginTop: theme.spacing(1.5),
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        borderRadius: 12,
        minHeight: 44,
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    infoLabel: {
        fontSize: '0.75rem',
        color: theme.palette.grey[600],
        marginBottom: 4,
    },
    infoValue: {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.text.primary,
        wordBreak: 'break-word',
    },
    infoTextBlock: {
        flex: 1,
        minWidth: 0,
    },
    iconRoundButton: {
        width: 44,
        height: 44,
        padding: 0,
    },
    notesHint: {
        fontSize: '0.75rem',
        color: theme.palette.grey[600],
        marginBottom: theme.spacing(1.5),
    },
    notesTextarea: {
        width: '100%',
        boxSizing: 'border-box',
        margin: 0,
        padding: theme.spacing(1.5),
        minHeight: 88,
        fontFamily: theme.typography.fontFamily,
        fontSize: '0.875rem',
        lineHeight: 1.5,
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: 12,
        resize: 'none',
        outline: 'none',
        transition: theme.transitions.create(['box-shadow', 'border-color'], {
            duration: theme.transitions.duration.shorter,
        }),
        '&::placeholder': {
            color: theme.palette.grey[500],
            opacity: 1,
        },
        '&:focus': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
        },
    },
    saveNoteButton: {
        width: '100%',
        marginTop: theme.spacing(1.5),
        border: `2px solid ${theme.palette.primary.main}`,
        color: theme.palette.text.primary,
        fontWeight: 600,
        fontSize: '0.875rem',
        textTransform: 'none',
        borderRadius: 12,
        minHeight: 44,
    },
    emptyOrders: {
        textAlign: 'center',
        color: theme.palette.grey[600],
        fontSize: '0.875rem',
        padding: theme.spacing(2),
    },
}));

export default useStyles;
